/**
 * jwt.ts - the backend embeds fine-grained role names (SUPER_ADMIN,
 * VERIFICATION_OFFICER, CORPORATE_ADMIN, PARTNER, DRIVER, CLIENT, ...) as a
 * `roles` claim inside the JWT access token itself (JwtService.java), not in
 * the `/users/me` or login response body - `AuthUserResponse.accountType` is
 * only the coarse CLIENT/DRIVER/PARTNER/STAFF/CORPORATE enum. This is the
 * one place that decodes that claim back out, so `RequireRole` (Section 6.1)
 * can check real role names instead of the coarse account type.
 *
 * No signature verification here - the token only ever came from our own
 * backend over HTTPS moments earlier, and this is UX-only (Section 6.2's
 * "the backend remains the sole source of truth").
 */
function decodeAccessTokenPayload(accessToken: string): Record<string, unknown> | null {
  try {
    const payloadSegment = accessToken.split('.')[1];
    const base64 = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + c.charCodeAt(0).toString(16).padStart(2, '0'))
        .join(''),
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function decodeStringArrayClaim(accessToken: string, claim: string): string[] {
  const payload = decodeAccessTokenPayload(accessToken);
  const value = payload?.[claim];
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : [];
}

export function decodeAccessTokenRoles(accessToken: string): string[] {
  return decodeStringArrayClaim(accessToken, 'roles');
}

/**
 * decodeAccessTokenPermissions - the RBAC->PBAC migration's `permissions` claim (union of every
 * permission granted by the user's assigned roles, computed server-side - see JwtService). Same
 * UX-only decode as decodeAccessTokenRoles: the backend independently enforces every permission check
 * via hasAuthority(...) on every request regardless of what this returns.
 */
export function decodeAccessTokenPermissions(accessToken: string): string[] {
  return decodeStringArrayClaim(accessToken, 'permissions');
}
